import PlusIcon from "@mui/icons-material/Add";
import UnlimitedIcon from "@mui/icons-material/AllInclusive";
import MinusIcon from "@mui/icons-material/Remove";
import ResetIcon from "@mui/icons-material/Undo";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Big from "big.js";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../../context/CaseContext";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { ZERO, max } from "../../../shared/bigUtil";
import { Configuration, NamedEntity } from "../../../shared/CaseModel";
import { percentage } from "../../../shared/percentage";
import { CurrencyTextField } from "../../general/CurrencyTextField";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { SectionLabel } from "../../general/SectionLabel";
import { SegmentedControl } from "../../general/SegmentedControl";
import { TonalBadge } from "../../general/TonalBadge";
import { WinChanceSlider } from "../../issues/WinChanceSlider";
import { areEqualWinChances } from "./Configuration";

/** A claim as far as its win chances are varied. */
export type BargainingClaim = {
  id: string;
  title: string;
  claimant: NamedEntity;
  defendants: NamedEntity[];
  // what carries its win chances, as the calculation decides them: its issues,
  // or the claim itself where it has none (then the one leaf has its id)
  leaves: NamedEntity[];
};

/** The win chances of one leaf of a claim. */
type LeafChances = {
  leaf: NamedEntity;
  // the leaf is an issue of the claim, not the claim itself
  isIssue: boolean;
  chances: Chance[];
};

type ParameterKind = "winChance" | "costs" | "assets";

/** One win chance of a claim as the variant has it. */
type Chance = {
  key: string;
  label: string;
  // says what the label means, where it is not a name
  hint?: string;
  // whom the chance is against, where it is against one defendant
  opponentName?: string;
  chance: number;
  baseline?: number;
  set: (newChance: number) => void;
};

/** The buttons beside an amount move it by this share of its baseline. */
const STEP = 0.1;

/**
 * A party without a limit of assets has no baseline to step from: its amount
 * moves by the step of this one.
 */
const UNLIMITED_STEP_BASE = 1000000;

/** How a difference in points is written: without a sign, with up to two decimals. */
const POINTS = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 });

const copyOf = (configuration: Configuration): Configuration =>
  JSON.parse(JSON.stringify(configuration));

/** A parameter of a party: its name over its baseline, then what sets it. */
const PartyParameter = ({
  name,
  baseline,
  children,
}: {
  name: string;
  // the baseline of the parameter, as it is written
  baseline: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.bargaining",
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 2,
        rowGap: 1,
        px: 2.5,
        py: 1.375,
        "& + &": { borderTop: 1, borderColor: "divider" },
      }}
    >
      <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
        >
          {t("baselineOf", { value: baseline })}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

/**
 * The parameters of the modified variant, one kind at a time: the win chances
 * of the claims, the costs of the parties and their assets. Each shows its
 * baseline and how far it is from it, and can be set back to it.
 */
export const BargainingParameters = ({
  claims,
  partyNames,
  variant,
  baseline,
  onChange,
  compact = false,
}: {
  claims: BargainingClaim[];
  partyNames: { [partyId: string]: string };
  variant: Configuration;
  baseline: Configuration;
  // called with the variant as it is to be from now on
  onChange: (newVariant: Configuration) => void;
  // without the buttons that step an amount, where there is no room for them
  compact?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  // the variant is saved with the case: who may only read the case sees the
  // variant as it is stored and cannot vary it
  const { readOnly } = useCase();
  const [kind, setKind] = useState<ParameterKind>("winChance");

  const change = (alter: (copy: Configuration) => void) => {
    const copy = copyOf(variant);
    alter(copy);
    onChange(copy);
  };
  const nameOf = (partyId: string) => partyNames[partyId] ?? partyId;

  // the win chances of a leaf — an issue, or a claim without issues: one for
  // all defendants of a generic leaf, otherwise one per defendant it runs
  // against
  const chancesOfLeaf = (
    claim: BargainingClaim,
    { id: leafId }: NamedEntity,
  ): Chance[] => {
    const chances = variant.winChances[leafId];
    const baselineChances = baseline.winChances[leafId];
    if (!chances) return [];
    const isIssue = leafId !== claim.id;
    if (chances.generic)
      return [
        {
          key: "generic",
          label: t("claims.kind.generic"),
          hint: t(`claims.kind.hint.${isIssue ? "issue" : "claim"}.generic`),
          chance: chances.genericWinChance,
          baseline: baselineChances?.genericWinChance,
          set: (newChance: number) =>
            change((copy) => {
              copy.winChances[leafId].genericWinChance = newChance;
            }),
        },
      ];
    // who the leaf runs against comes from the case, never from the what-if
    const involved = Object.keys(baselineChances?.perDefendantWinChance ?? {});
    return claim.defendants
      .filter(({ id }) => involved.includes(id))
      .map(({ id, name }) => ({
        key: id,
        label: name,
        opponentName: name,
        chance: chances.perDefendantWinChance[id] ?? 0,
        baseline: baselineChances?.perDefendantWinChance[id],
        set: (newChance: number) =>
          change((copy) => {
            copy.winChances[leafId].perDefendantWinChance[id] = newChance;
          }),
      }));
  };
  const leavesOf = (claim: BargainingClaim): LeafChances[] =>
    claim.leaves
      .map((leaf) => ({
        leaf,
        isIssue: leaf.id !== claim.id,
        chances: chancesOfLeaf(claim, leaf),
      }))
      .filter(({ chances }) => chances.length > 0);

  const changed: { [kind in ParameterKind]: number } = {
    winChance: claims
      .flatMap(leavesOf)
      .flatMap(({ chances }) => chances)
      .filter(({ chance, baseline }) => chance !== baseline).length,
    costs: Object.keys(variant.costs).filter(
      (partyId) =>
        (variant.costs[partyId] ?? 0) !== (baseline.costs[partyId] ?? 0),
    ).length,
    assets: Object.keys(variant.assets).filter(
      (partyId) =>
        (variant.assets[partyId] ?? "unlimited") !==
        (baseline.assets[partyId] ?? "unlimited"),
    ).length,
  };
  const isChanged = Object.values(changed).some((count) => count > 0);

  const changeBadge = (isOff: boolean, text: string) => (
    <TonalBadge
      tone={isOff ? theme.palette.primary : undefined}
      sx={{ minWidth: "7.5em", justifyContent: "center", ...TABULAR_NUMERALS }}
    >
      {text}
    </TonalBadge>
  );
  // takes its room whether there is anything to set back or not, so the rows
  // keep their columns
  const resetButton = (isOff: boolean, title: string, onClick: () => void) => (
    <IconButton
      size="small"
      disabled={readOnly}
      onClick={onClick}
      title={title}
      aria-label={title}
      sx={{ visibility: isOff ? "visible" : "hidden" }}
    >
      <ResetIcon fontSize="small" />
    </IconButton>
  );
  const stepButton = (
    direction: "minus" | "plus",
    of: "costs" | "assets",
    partyId: string,
    step: Big,
    disabled: boolean,
    onClick: () => void,
  ) =>
    !compact && (
      <Button
        variant="outlined"
        size="small"
        color="inherit"
        disabled={disabled || readOnly}
        startIcon={direction === "minus" ? <MinusIcon /> : <PlusIcon />}
        onClick={onClick}
        title={t(
          `analytics.settlement.bargaining.buttons.${direction}.${of}.tooltip`,
          { name: nameOf(partyId), step: currency.format(step.toNumber()) },
        )}
        sx={{ borderColor: "divider", ...TABULAR_NUMERALS }}
      >
        {percentage.format(STEP)}
      </Button>
    );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          useFlexGap
          spacing={1.5}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <SectionLabel component="h2">
            {t("analytics.settlement.bargaining.chart.variant")}
          </SectionLabel>
          <SegmentedControl
            value={kind}
            onChange={setKind}
            ariaLabel={t("analytics.settlement.bargaining.parameters")}
            options={(["winChance", "costs", "assets"] as const).map(
              (value) => ({
                value,
                label: (
                  <Stack
                    direction="row"
                    spacing={0.875}
                    sx={{ alignItems: "center" }}
                  >
                    <span>
                      {t(
                        `analytics.settlement.bargaining.configurationTabs.${value}`,
                      )}
                    </span>
                    {/* how many of the kind are off their baseline */}
                    {changed[value] > 0 && (
                      <TonalBadge
                        tone={theme.palette.primary}
                        sx={{ px: 0.75, py: 0, ...TABULAR_NUMERALS }}
                      >
                        {changed[value]}
                      </TonalBadge>
                    )}
                  </Stack>
                ),
              }),
            )}
          />
        </Stack>
        <Button
          size="small"
          startIcon={<ResetIcon />}
          disabled={!isChanged || readOnly}
          title={t("analytics.settlement.bargaining.buttons.reset.all.tooltip")}
          onClick={() => onChange(copyOf(baseline))}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("analytics.settlement.bargaining.buttons.reset.all.label")}
        </Button>
      </Box>

      {kind === "winChance" &&
        claims.map((claim, index) => {
          const leaves = leavesOf(claim);
          if (leaves.length === 0) return null;
          const isOff = leaves.some(
            ({ leaf }) =>
              !areEqualWinChances(
                variant.winChances[leaf.id],
                baseline.winChances[leaf.id],
              ),
          );
          return (
            <Box
              key={claim.id}
              sx={{ "& + &": { borderTop: 1, borderColor: "divider" } }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {`${index + 1}. ${t(
                      "analytics.settlement.bargaining.claim",
                      { title: claim.title },
                    )}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("analytics.settlement.titleSettlementChart", {
                      name: claim.claimant.name,
                      other: claim.defendants
                        .map(({ name }) => name)
                        .join(", "),
                    })}
                  </Typography>
                </Box>
                {resetButton(
                  isOff,
                  t(
                    "analytics.settlement.bargaining.buttons.reset.winChance.tooltip",
                  ),
                  () =>
                    change((copy) => {
                      leaves.forEach(({ leaf }) => {
                        copy.winChances[leaf.id] = JSON.parse(
                          JSON.stringify(baseline.winChances[leaf.id]),
                        );
                      });
                    }),
                )}
              </Box>
              {leaves.map(({ leaf, isIssue, chances }) => (
                <Box key={leaf.id} sx={{ pb: 0.5 }}>
                  {/* an issue is named over its chances, as on the claims
                      page; a claim without issues has its name above */}
                  {isIssue && (
                    <Typography
                      variant="body2"
                      sx={{ px: 2.5, pt: 1, fontWeight: 600 }}
                    >
                      {leaf.name}
                    </Typography>
                  )}
                  {chances.map(
                    ({
                      key,
                      label,
                      hint,
                      opponentName,
                      chance,
                      baseline,
                      set,
                    }) => (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          columnGap: 2,
                          rowGap: 0.5,
                          px: 2.5,
                          pl: isIssue ? 4.5 : 2.5,
                          py: 1,
                        }}
                      >
                        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                          <Tooltip title={hint ?? ""}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, width: "fit-content" }}
                            >
                              {label}
                            </Typography>
                          </Tooltip>
                          {baseline !== undefined && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                ...TABULAR_NUMERALS,
                              }}
                            >
                              {t("analytics.settlement.bargaining.baselineOf", {
                                value: percentage.format(baseline),
                              })}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: "2.4 1 300px", minWidth: 0 }}>
                          <WinChanceSlider
                            // the slider keeps its own value while it is dragged
                            // and takes a stored one only anew
                            key={variant.timestamp}
                            chance={chance}
                            baseline={baseline}
                            opponentName={opponentName}
                            onChanceChange={set}
                            changeLabel={(points) =>
                              points === 0
                                ? t(
                                    "analytics.settlement.bargaining.valueLabels.unchanged",
                                  )
                                : t(
                                    "analytics.settlement.bargaining.valueLabels.points",
                                    {
                                      count: Math.abs(points),
                                      points: `${points > 0 ? "+" : "−"}${POINTS.format(Math.abs(points))}`,
                                    },
                                  )
                            }
                          />
                        </Box>
                      </Box>
                    ),
                  )}
                </Box>
              ))}
            </Box>
          );
        })}

      {kind === "costs" &&
        Object.keys(variant.costs).map((partyId) => {
          const cost = variant.costs[partyId] ?? 0;
          const baseValue = new Big(baseline.costs[partyId] ?? 0);
          const step = baseValue.mul(STEP);
          const delta = baseValue.neg().add(cost).toNumber();
          return (
            <PartyParameter
              key={partyId}
              name={nameOf(partyId)}
              baseline={currency.format(baseValue.toNumber())}
            >
              {stepButton(
                "minus",
                "costs",
                partyId,
                step,
                step.eq(0) || cost <= 0,
                () =>
                  change((copy) => {
                    copy.costs[partyId] = max(
                      step.neg().add(cost),
                      ZERO,
                    ).toNumber();
                  }),
              )}
              <CurrencyTextField
                key={variant.timestamp}
                ariaLabel={t("analytics.settlement.bargaining.costsOf", {
                  name: nameOf(partyId),
                })}
                currency={currency}
                variant="outlined"
                size="small"
                disabled={readOnly}
                onValueChange={(newValue) =>
                  change((copy) => {
                    copy.costs[partyId] = newValue;
                  })
                }
                value={cost}
                sx={{ width: theme.spacing(20) }}
              />
              {stepButton("plus", "costs", partyId, step, step.eq(0), () =>
                change((copy) => {
                  copy.costs[partyId] = step.add(cost).toNumber();
                }),
              )}
              {changeBadge(
                delta !== 0,
                delta === 0
                  ? t("analytics.settlement.bargaining.valueLabels.unchanged")
                  : `${delta > 0 ? "+" : ""}${currency.format(delta)}`,
              )}
              {resetButton(
                delta !== 0,
                t(
                  "analytics.settlement.bargaining.buttons.reset.costs.tooltip",
                  { name: nameOf(partyId) },
                ),
                () =>
                  change((copy) => {
                    copy.costs[partyId] = baseValue.toNumber();
                  }),
              )}
            </PartyParameter>
          );
        })}

      {kind === "assets" &&
        Object.keys(variant.assets).map((partyId) => {
          const baseValue = baseline.assets[partyId] ?? "unlimited";
          const assetValue = variant.assets[partyId] ?? "unlimited";
          const isOff = baseValue !== assetValue;
          const step = new Big(
            baseValue === "unlimited" ? UNLIMITED_STEP_BASE : baseValue,
          ).mul(STEP);
          const label = !isOff
            ? t("analytics.settlement.bargaining.valueLabels.unchanged")
            : baseValue === "unlimited"
              ? t("analytics.settlement.bargaining.valueLabels.limited")
              : assetValue === "unlimited"
                ? t("analytics.settlement.bargaining.valueLabels.unlimited")
                : `${assetValue - baseValue > 0 ? "+" : ""}${currency.format(
                    assetValue - baseValue,
                  )}`;
          return (
            <PartyParameter
              key={partyId}
              name={nameOf(partyId)}
              baseline={
                baseValue === "unlimited"
                  ? t(
                      "analytics.settlement.bargaining.buttons.unlimited.assets.label",
                    )
                  : currency.format(baseValue)
              }
            >
              {stepButton("minus", "assets", partyId, step, step.eq(0), () =>
                change((copy) => {
                  // an unlimited amount steps down from its baseline
                  const from =
                    assetValue === "unlimited"
                      ? baseValue === "unlimited"
                        ? 0
                        : baseValue
                      : assetValue;
                  copy.assets[partyId] = max(
                    step.neg().add(from),
                    ZERO,
                  ).toNumber();
                }),
              )}
              <CurrencyTextField
                key={variant.timestamp}
                ariaLabel={t("analytics.settlement.bargaining.assetsOf", {
                  name: nameOf(partyId),
                })}
                currency={currency}
                variant="outlined"
                size="small"
                disabled={readOnly}
                onValueChange={(newValue) =>
                  change((copy) => {
                    copy.assets[partyId] = newValue;
                  })
                }
                // an unlimited amount leaves the field empty
                value={assetValue === "unlimited" ? undefined : assetValue}
                sx={{ width: theme.spacing(20) }}
              />
              {stepButton(
                "plus",
                "assets",
                partyId,
                step,
                step.eq(0) || assetValue === "unlimited",
                () =>
                  change((copy) => {
                    if (assetValue === "unlimited") return;
                    copy.assets[partyId] = step.add(assetValue).toNumber();
                  }),
              )}
              <Button
                variant={assetValue === "unlimited" ? "contained" : "outlined"}
                size="small"
                color={assetValue === "unlimited" ? "primary" : "inherit"}
                startIcon={<UnlimitedIcon />}
                disabled={step.eq(0) || readOnly}
                onClick={() =>
                  change((copy) => {
                    copy.assets[partyId] = "unlimited";
                  })
                }
                title={t(
                  "analytics.settlement.bargaining.buttons.unlimited.assets.tooltip",
                  { name: nameOf(partyId) },
                )}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  ...(assetValue !== "unlimited" && { borderColor: "divider" }),
                }}
              >
                {t(
                  "analytics.settlement.bargaining.buttons.unlimited.assets.label",
                )}
              </Button>
              {changeBadge(isOff, label)}
              {resetButton(
                isOff,
                t(
                  "analytics.settlement.bargaining.buttons.reset.assets.tooltip",
                  { name: nameOf(partyId) },
                ),
                () =>
                  change((copy) => {
                    copy.assets[partyId] = baseValue;
                  }),
              )}
            </PartyParameter>
          );
        })}
    </Paper>
  );
};
